import { useState } from 'react';
import { parseXML, parseJSON } from '@capture/shared';
import type { XmlElement, SuggestTarget } from '@capture/shared';
import { EmptyState } from '@/components/EmptyState';
import './SampleTreePane.css';

interface Props {
  format: 'xml' | 'json';
  onNodeClick: (target: SuggestTarget) => void;
}

/**
 * Paste-a-sample panel. Parses the pasted XML or JSON text into a read-only
 * clickable tree. Clicking an element or attribute emits a `SuggestTarget`
 * which the parent typically pipes into `suggestXPath` / `suggestJsonPath`
 * to populate the active field's path input.
 */
export function SampleTreePane({ format, onNodeClick }: Props) {
  const [open, setOpen] = useState(false);
  const [raw, setRaw] = useState('');

  if (!open) {
    return (
      <div className="sample-tree-pane">
        <EmptyState
          title="No sample loaded"
          body={
            format === 'xml'
              ? 'Paste a sample XML header to preview the parsed tree and click nodes to seed the identity path.'
              : 'Paste a sample JSON header to preview the parsed tree and click nodes to seed the identity path.'
          }
          action={{ label: 'Paste sample', onClick: () => setOpen(true) }}
        />
      </div>
    );
  }

  const parsed = raw.trim() === '' ? null : safeParse(raw, format);

  return (
    <div className="sample-tree-pane sample-tree-pane--open">
      <label className="sample-tree-pane__label">
        <span>Sample {format.toUpperCase()}</span>
        <textarea
          className="sample-tree-pane__textarea"
          role="textbox"
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          placeholder={
            format === 'xml' ? '<Invoice class="INV"/>' : '{"kind": "INV"}'
          }
          rows={4}
        />
      </label>
      {parsed && !parsed.ok && (
        <div className="sample-tree-pane__error" role="alert">
          Invalid {format}: {parsed.error}
        </div>
      )}
      {parsed && parsed.ok && format === 'xml' && 'doc' in parsed && (
        <XmlTree
          el={parsed.doc.root}
          path={[parsed.doc.root.name]}
          onClick={onNodeClick}
        />
      )}
      {parsed && parsed.ok && format === 'json' && 'root' in parsed && (
        <JsonTree value={parsed.root} pointer={[]} onClick={onNodeClick} />
      )}
    </div>
  );
}

function safeParse(
  raw: string,
  format: 'xml' | 'json',
): ReturnType<typeof parseXML> | ReturnType<typeof parseJSON> {
  try {
    return format === 'xml' ? parseXML(raw) : parseJSON(raw);
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
}

function XmlTree({
  el,
  path,
  onClick,
}: {
  el: XmlElement;
  path: string[];
  onClick: (t: SuggestTarget) => void;
}) {
  return (
    <ul className="sample-tree-pane__tree">
      <li>
        <button
          type="button"
          className="sample-tree-pane__node sample-tree-pane__node--elem"
          onClick={() => onClick({ kind: 'elem', elementPath: path })}
        >
          {el.name}
        </button>
        {Object.entries(el.attrs).map(([k]) => (
          <button
            key={k}
            type="button"
            className="sample-tree-pane__node sample-tree-pane__node--attr"
            onClick={() =>
              onClick({ kind: 'attr', elementPath: path, attrName: k })
            }
          >
            @{k}
          </button>
        ))}
        {el.children.map((c, i) => (
          <XmlTree
            key={`${c.name}-${i}`}
            el={c}
            path={[...path, c.name]}
            onClick={onClick}
          />
        ))}
      </li>
    </ul>
  );
}

// JSON tree uses SuggestTarget's elem/attr shape loosely — the JSON
// suggester is the real consumer and will rebuild a path from the
// pointer via suggestJsonPath. Here we just expose click events;
// the parent wires them to suggestJsonPath.
function JsonTree({
  value,
  pointer,
  onClick,
}: {
  value: unknown;
  pointer: Array<string | number>;
  onClick: (t: SuggestTarget) => void;
}) {
  if (value === null || typeof value !== 'object') {
    return (
      <div className="sample-tree-pane__leaf">
        = <span className="sample-tree-pane__value">{JSON.stringify(value)}</span>
      </div>
    );
  }
  if (Array.isArray(value)) {
    return (
      <ul className="sample-tree-pane__tree">
        {value.map((v, i) => (
          <li key={i}>
            <button
              type="button"
              className="sample-tree-pane__node sample-tree-pane__node--idx"
              onClick={() =>
                onClick({
                  kind: 'elem',
                  elementPath: [...pointer, i].map(String),
                })
              }
            >
              [{i}]
            </button>
            <JsonTree value={v} pointer={[...pointer, i]} onClick={onClick} />
          </li>
        ))}
      </ul>
    );
  }
  return (
    <ul className="sample-tree-pane__tree">
      {Object.entries(value as Record<string, unknown>).map(([k, v]) => (
        <li key={k}>
          <button
            type="button"
            className="sample-tree-pane__node sample-tree-pane__node--key"
            onClick={() =>
              onClick({
                kind: 'elem',
                elementPath: [...pointer, k].map(String),
              })
            }
          >
            {k}
          </button>
          <JsonTree value={v} pointer={[...pointer, k]} onClick={onClick} />
        </li>
      ))}
    </ul>
  );
}
